import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import type { CustomerListRecord, CustomersGateway } from '@forq/customers-data';

interface CustomerSearchFieldProps {
  readonly tenantId: string;
  readonly gateway: CustomersGateway;
  /** Called with the picked customer, or null when the selection is cleared/edited. */
  readonly onSelect: (customer: { readonly number: string; readonly name: string } | null) => void;
  readonly placeholder?: string;
  readonly autoFocus?: boolean;
  /** Vorbelegung des sichtbaren Feldes (z. B. beim Bearbeiten eines bestehenden Vorgangs). */
  readonly initialLabel?: string;
}

/**
 * Reusable real-time customer autocomplete — type a name/number/city and pick from live suggestions
 * (tenant-scoped, debounced). Used by Mietpark and Sales instead of separate number/name fields.
 */
export function CustomerSearchField({ tenantId, gateway, onSelect, placeholder = 'Kunde suchen – Name, Nummer, Ort …', autoFocus, initialLabel }: CustomerSearchFieldProps) {
  const [query, setQuery] = useState(initialLabel ?? '');
  const [results, setResults] = useState<readonly CustomerListRecord[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const picked = useRef(false);

  useEffect(() => {
    if (picked.current) { picked.current = false; return; }
    const term = query.trim();
    if (!open || !term) { setResults([]); return undefined; }
    const timer = window.setTimeout(() => {
      gateway.searchCustomers({ tenantId, query: term, status: 'all', pageSize: 6 })
        .then((page) => { setResults(page.items); setActive(0); })
        .catch(() => setResults([]));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [query, open, tenantId, gateway]);

  function pick(customer: CustomerListRecord): void {
    picked.current = true;
    setQuery(`${customer.number} · ${customer.displayName}`);
    setResults([]);
    setOpen(false);
    onSelect({ number: customer.number, name: customer.displayName });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((current) => Math.min(current + 1, results.length - 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive((current) => Math.max(current - 1, 0)); }
    else if (event.key === 'Enter' && results[active]) { event.preventDefault(); pick(results[active]); }
    else if (event.key === 'Escape') { setOpen(false); }
  }

  return (
    <div className="customer-search">
      <span className="customer-search__icon"><Search size={15} /></span>
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
        onChange={(event) => { setQuery(event.target.value); setOpen(true); onSelect(null); }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={handleKeyDown}
      />
      {open && results.length > 0 && (
        <ul className="customer-search__results">
          {results.map((customer, index) => (
            <li key={customer.id}>
              <button type="button" className={index === active ? 'is-active' : ''} onMouseDown={(event) => { event.preventDefault(); pick(customer); }} onMouseMove={() => setActive(index)}>
                <span className="customer-search__name">{customer.displayName}</span>
                <span className="customer-search__meta">{customer.number}{customer.city ? ` · ${customer.city}` : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
